import type { INotification } from '@featit/shared';
import type { WaitingNoticeCountsType } from '@featit/shared/notification-destination';
import { ApiService } from './ApiService';
import { expectList } from './answerShape';

/** What `GET /notifications` answers: a page, not a bare list. */
export interface INotificationPage {
  data: INotification[];
  total: number;
  /** I-2382 (AC-NB17): with `waiting`, every waiting notice counted by place and work area. */
  waiting?: WaitingNoticeCountsType;
}

/** A delivery fault as the administrator it was sent to reads it (T-0600, DM-19). */
export interface IDeliveryFaultDetail {
  id: string;
  /** The check that found it: `client-errors`, `mail-stuck`, `push-stuck`, `push-gateway-off`,
   * `feedback-target`, `feedback-misfiled`, `job-stale`. */
  kind: string;
  message: string;
  occurrences: number;
  openedAt: string;
  lastSeenAt: string;
  /** Null while it still holds. */
  clearedAt: string | null;
}

/** A delivery check held back for a planned deploy or maintenance window (I-1609, DM-33). */
export interface IDeliveryPauseDetail {
  key: string;
  kind: string;
  deploymentId: string;
  message: string;
  pausedUntil: string;
  firstSeenAt: string;
  lastSeenAt: string;
}

class NotificationApiService extends ApiService {
  /**
   * The endpoint has always returned `{ data, total }` while this was typed as an array, so the
   * store assigned an object to a list and the dropdown's `.some(…)` would have thrown on the
   * first notification that ever arrived. Nothing produced one until now, which is why nobody hit
   * it (I-0178).
   */
  findAll(
    unreadOnly?: boolean,
    limit?: number,
    offset?: number,
    /** F-0744 (AC-NB11): only these kinds; the server refuses one it does not know. */
    types?: readonly string[],
    /** I-2382 (AC-NB16): only what still waits on the reader, with the count of all of it. */
    waiting = false,
  ): Promise<INotificationPage> {
    return this.get('/notifications', {
      params: {
        unreadOnly,
        limit,
        offset,
        ...(types ? { types: types.join(',') } : {}),
        ...(waiting ? { waiting: true } : {}),
      },
    });
  }

  /** `criticalCount` (F-0206) is absent from a server that predates severity. */
  getUnreadCount(): Promise<{ count: number; criticalCount?: number }> {
    return this.get('/notifications/unread-count');
  }

  /** The delivery faults this person was told about: what a Needs you opens (DM-19). */
  async deliveryFaults(): Promise<IDeliveryFaultDetail[]> {
    return expectList<IDeliveryFaultDetail>(
      (await this.get<{ data?: unknown }>('/notifications/delivery-faults')).data,
      'Delivery problems',
    );
  }

  /** The checks the monitor is holding back for a planned window; administrators only (DM-33). */
  async deliveryPauses(): Promise<IDeliveryPauseDetail[]> {
    return expectList<IDeliveryPauseDetail>(
      (await this.get<{ data?: unknown }>('/notifications/delivery-pauses')).data,
      'Delivery problems',
    );
  }

  markAsRead(notificationId: string): Promise<void> {
    return this.patch(`/notifications/${notificationId}/read`);
  }
}

export const notificationApi = new NotificationApiService();
